import { http } from './http'
import {
  ApplePayConfig,
  GooglePayConfig,
  ICurrency,
  IGroup,
  IGroupCurrencyDetails,
  IGroupFestival,
  IGroupFestivalPayload,
  PaymentProvider,
} from '../../common/models/groups'

export const getGroups = () => http.get<IGroup[]>('/operations/festival-groups')

export const updateGroup = (id: string, name: string) =>
  http.put<IGroup>(`/operations/festival-groups/${id}`, { name })

export const createGroup = (name: string) =>
  http.post<IGroup>(`/operations/festival-groups`, { name })

export const deleteGroup = (id: string) =>
  http.delete<IGroup>(`/operations/festival-groups/${id}`)

export const updateGroupFestival = (
  festivalGroupId: string,
  festivalId: string,
  data: IGroupFestivalPayload,
) =>
  http.put<IGroupFestival>(
    `/operations/festival-groups/${festivalGroupId}/festivals/${festivalId}`,
    data,
  )

export const deleteGroupFestival = (
  festivalGroupId: string,
  festivalId: string,
) =>
  http.delete<IGroupFestival>(
    `/operations/festival-groups/${festivalGroupId}/festivals/${festivalId}`,
  )

export const updateGroupCurrency = (
  festivalGroupId: string,
  currencyId: string,
  data: ICurrency,
) =>
  http.put<ICurrency>(
    `/operations/festival-groups/${festivalGroupId}/currencies/${currencyId}`,
    data,
  )

export const deleteGroupCurrency = (
  festivalGroupId: string,
  currencyId: string,
) =>
  http.delete<ICurrency>(
    `/operations/festival-groups/${festivalGroupId}/currencies/${currencyId}`,
  )

export const updateGroupCurrencyDetails = (
  festivalGroupId: string,
  data: IGroupCurrencyDetails[],
) =>
  http.put<ICurrency>(
    `/operations/festival-groups/${festivalGroupId}/currencies/details`,
    data,
  )

export const createGroupCurrency = (
  festivalGroupId: string,
  data: Omit<ICurrency, 'id'>,
  festivalName?: string,
) =>
  http.post<ICurrency>(
    `/operations/festival-groups/${festivalGroupId}/currencies`,
    data,
    festivalName ? { headers: { 'x-festival-name': festivalName } } : undefined,
  )

export const getPaymentProviders = () =>
  http.get<PaymentProvider[]>('/operations/payment-providers')

export const createPaymentProvider = (data: Omit<PaymentProvider, 'id'>) =>
  http.post<PaymentProvider[]>('/operations/payment-providers', data)

export const updatePaymentProvider = (
  id: string,
  data: Omit<PaymentProvider, 'id'>,
) => http.put<PaymentProvider[]>(`/operations/payment-providers/${id}`, data)

export const deletePaymentProvider = (id: string) =>
  http.delete<void>(`/operations/payment-providers/${id}`)

export const getApplePayConfig = (
  paymentProviderId: string,
  festivalName?: string,
) =>
  http.get<ApplePayConfig>(`/operations/apple-pay-configs`, {
    params: {
      paymentProviderId,
    },
    headers: festivalName ? { 'x-festival-name': festivalName } : undefined,
  })

// The Apple Pay config endpoints are multipart: the JSON config goes in the
// `config` part, with the optional merchant-identity `certificate`/`privateKey`
// files alongside it. Pass `clearKeys` on update to drop the stored key pair.
const applePayConfigFormData = (
  data: Omit<ApplePayConfig, 'id'>,
  certificate?: File | null,
  privateKey?: File | null,
) => {
  const formData = new FormData()
  formData.append(
    'config',
    new Blob([JSON.stringify(data)], { type: 'application/json' }),
  )
  if (certificate) formData.append('certificate', certificate)
  if (privateKey) formData.append('privateKey', privateKey)
  return formData
}

export const createApplePayConfig = (
  data: Omit<ApplePayConfig, 'id'>,
  certificate?: File | null,
  privateKey?: File | null,
) =>
  http.post<ApplePayConfig>(
    `/operations/apple-pay-configs`,
    applePayConfigFormData(data, certificate, privateKey),
  )

export const updateApplePayConfig = (
  configId: string,
  data: Omit<ApplePayConfig, 'id'>,
  certificate?: File | null,
  privateKey?: File | null,
  clearKeys?: boolean,
) =>
  http.put<ApplePayConfig>(
    `/operations/apple-pay-configs/${configId}`,
    applePayConfigFormData(data, certificate, privateKey),
    clearKeys ? { params: { clearKeys: true } } : undefined,
  )

export const deleteApplePayConfig = (configId: string) =>
  http.delete<void>(`/operations/apple-pay-configs/${configId}`)

export const getGooglePayConfig = (
  paymentProviderId: string,
  festivalName?: string,
) =>
  http.get<GooglePayConfig>(`/operations/google-pay-configs`, {
    params: {
      paymentProviderId,
    },
    headers: festivalName ? { 'x-festival-name': festivalName } : undefined,
  })

export const createGooglePayConfig = (data: Omit<GooglePayConfig, 'id'>) =>
  http.post<GooglePayConfig>(`/operations/google-pay-configs`, data)

export const updateGooglePayConfig = (
  configId: string,
  data: Omit<GooglePayConfig, 'id'>,
) =>
  http.put<GooglePayConfig>(`/operations/google-pay-configs/${configId}`, data)

export const deleteGooglePayConfig = (configId: string) =>
  http.delete<void>(`/operations/google-pay-configs/${configId}`)
